import type { ReviewWithReplyDto } from '@up2stay/up2promo-api-client';
import {
  Button,
  Card,
  cn,
  previewPanelSurfaceClass,
  Textarea,
} from '@up2stay/up2promo-ui';
import { Loader2, RefreshCw, Send, Sparkles } from 'lucide-react';
import { useState } from 'react';

import type { ReviewsInbox } from '@/core/components/reputation/reviews/use-reviews-inbox';
import { useEffectiveReplyStyle } from '@/core/hooks/use-effective-reply-style';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { shellPaths } from '@/core/router/app-paths';

/**
 * Reply editor with AI drafting. Drafts follow the reply style configured in
 * Setup (template or own wording) — there is deliberately no second style
 * control here. Mount with `key={review.id}` so text state resets per review.
 */
export function ReviewReplyComposer({
  inbox,
  review,
  initialText,
  onDone,
  className,
}: {
  inbox: ReviewsInbox;
  review: ReviewWithReplyDto;
  /** Prefill — server draft, or the published reply when editing. */
  initialText: string;
  /** Called after a successful publish. */
  onDone?: () => void;
  className?: string;
}) {
  const t = inbox.reviewsDict.draft;
  const { isFeatureEnabled } = useEntitlements();
  const aiEnabled = isFeatureEnabled('feature.reputation_ai');
  const style = useEffectiveReplyStyle(review.locationId);
  const { navigateShell } = useNavigateShell();

  const [text, setText] = useState(initialText);

  const suggesting = inbox.isSuggesting(review.id);
  const publishing = inbox.isPublishing(review.id);
  const busy = suggesting || publishing;

  const generate = async () => {
    try {
      setText(await inbox.suggest(review));
    } catch {
      // Toast already shown by the mutation.
    }
  };

  const publish = async () => {
    await inbox.publish(review, text.trim());
    onDone?.();
  };

  const styleLabel =
    style.kind === 'template' && style.templateName
      ? style.templateName
      : style.kind === 'custom'
        ? t.styleCustom
        : t.styleNone;

  return (
    <Card
      className={cn(
        'flex flex-col gap-3.5 p-4 shadow-none sm:p-[18px]',
        previewPanelSurfaceClass,
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="flex size-7 items-center justify-center rounded-lg bg-[color:var(--ghost-accent-icon-bg)] text-[color:var(--ghost-accent-fg)]">
          <Sparkles className="size-[15px]" aria-hidden />
        </span>
        <h3 className="flex-1 text-[14.5px] font-bold">{t.title}</h3>
        {aiEnabled ? (
          <p className="text-muted-foreground text-[12px]">
            {t.styleLabel}:{' '}
            <span className="font-medium text-foreground">{styleLabel}</span>
            {' · '}
            <button
              type="button"
              className="text-[color:var(--ghost-accent-fg)] hover:underline"
              onClick={() =>
                navigateShell(
                  `${shellPaths.reputationSetup}?tab=responses&locationId=${review.locationId}`,
                )
              }
            >
              {t.styleChange}
            </button>
          </p>
        ) : null}
      </div>

      <div className="relative">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={suggesting ? t.generating : t.placeholder}
          rows={5}
          disabled={busy}
          className="min-h-[120px] rounded-[var(--radius-nested)] bg-card px-4 py-3.5 text-sm leading-[1.65]"
        />
        {suggesting ? (
          <div className="bg-card/70 text-muted-foreground absolute inset-0 flex items-center justify-center gap-2 rounded-[var(--radius-nested)] text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            {t.generating}
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2.5">
        {aiEnabled ? (
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={generate}
          >
            {text.trim() ? (
              <RefreshCw className="mr-1.5 size-3.5" aria-hidden />
            ) : (
              <Sparkles className="mr-1.5 size-3.5" aria-hidden />
            )}
            {text.trim() ? t.regenerate : t.create}
          </Button>
        ) : null}
        <Button size="sm" disabled={busy || !text.trim()} onClick={publish}>
          {publishing ? (
            <Loader2 className="mr-1.5 size-3.5 animate-spin" aria-hidden />
          ) : (
            <Send className="mr-1.5 size-3.5" aria-hidden />
          )}
          {publishing
            ? inbox.reviewsDict.reply.publishing
            : inbox.reviewsDict.reply.publish}
        </Button>
      </div>
    </Card>
  );
}
